// @ts-strict-ignore
import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Checkbox,
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  Skeleton,
  TextField,
  Typography,
} from "@aviary";
import { useTypeaheadSearchLazyQuery } from "@shared/data/queries/TypeaheadSearchQuery/TypeaheadSearch.query";
import { useDebounce } from "@shared/hooks/useDebounce/useDebounce";
import { useKeyboardNavigationList } from "@shared/hooks/useKeyboardNavigationList/useKeyboardNavigationList";
import { l } from "@shared/locales/i18n";
import { useSearchSessionIdVar } from "@shared/reactiveVar/useSearchSessionId/useSearchSessionId.store";
import { Size } from "@shared/types/sizes";

import * as styles from "./IngredientSearch.styles";

const RESULTS_TO_SHOW = 6;

interface Props {
  onSelectIngredient: (ingredientId: string, ingredientName: string) => void;
  isIngredientSelected: (ingredientId: string) => boolean;
  isContextual?: boolean;
}

const IngredientSearch = ({
  onSelectIngredient,
  isIngredientSelected,
  isContextual = false,
}: Props) => {
  const { t } = useTranslation();

  const [searchForIngredients, { loading, data }] = useTypeaheadSearchLazyQuery();

  const [searchText, setSearchText] = useState("");
  const [searchBarFocus, setSearchBarFocus] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectedRef = useRef(null);

  const searchSessionId = useSearchSessionIdVar();

  const resultsData = data?.viewer?.typeaheadSearch?.nodes ?? [];

  const debouncedSearch = useDebounce(
    (debouncedSearchText, catalogSearchSessionId) =>
      searchForIngredients({
        variables: {
          query: debouncedSearchText,
          first: RESULTS_TO_SHOW,
          filters: { list: ["INGREDIENTS"] },
          ...(catalogSearchSessionId && { searchSessionId: catalogSearchSessionId }),
        },
      }),
    400
  );

  useEffect(() => {
    if (searchText) {
      debouncedSearch(searchText, searchSessionId);
    }

    return () => debouncedSearch.cancel();
  }, [searchText]);

  const onEnter = () => {
    if (searchText === "" || resultsData.length === 0) return;
    const ingredient = resultsData[focusIndex];
    onSelectIngredient(ingredient?.id, ingredient?.name);
  };

  const clearField = () => setSearchText("");

  const { itemSelectedIndex: focusIndex, onKeyDown } = useKeyboardNavigationList(
    RESULTS_TO_SHOW,
    null,
    null,
    onEnter,
    null,
    clearField
  );

  const renderSkeletons = (numOfSkeletons: number): ReactNode[] =>
    Array.from({ length: numOfSkeletons }).map((_, i) => (
      <Skeleton
        css={styles.skeleton}
        type="typography"
        typographySize="body"
        margin="top"
        isFullWidth
        key={i}
      />
    ));

  const searchResults = () => {
    if (searchText === "") return null;

    if (loading) {
      return (
        <DropdownContent>
          <div>{renderSkeletons(RESULTS_TO_SHOW)}</div>
        </DropdownContent>
      );
    }

    if (resultsData.length === 0) {
      return (
        <DropdownContent>
          <div css={styles.noResultsFound}>
            <Typography type="h5">
              {isContextual
                ? t(l.sharedCatalog.noResults.NoIngredientsInResults)
                : t(l.sharedCatalog.noResults.NoResultsFound)}
            </Typography>
            <Button isOutlined size={Size.SMALL} isColor="system" onClick={clearField}>
              {t(l.sharedCatalog.noResults.ClearSearch)}
            </Button>
          </div>
        </DropdownContent>
      );
    }

    const renderCheckboxes = resultsData.map((ingredient, index) => (
      <div
        key={ingredient?.id}
        css={[styles.checkbox, focusIndex === index && styles.checkboxFocused]}
        ref={focusIndex !== null && focusIndex === index ? selectedRef : null}
      >
        <Checkbox
          isFullWidth
          onChange={() => onSelectIngredient(ingredient?.id, ingredient?.name)}
          checked={isIngredientSelected(ingredient?.id)}
        >
          {ingredient?.name}
        </Checkbox>
      </div>
    ));

    return (
      <div css={styles.dropdownContent}>
        <DropdownContent>{renderCheckboxes}</DropdownContent>
      </div>
    );
  };

  const onSearch = searchTerm => {
    setSearchBarFocus(true);
    setSearchText(searchTerm);
  };

  const handleFocus = () => {
    if (searchText !== "") {
      setSearchBarFocus(true);
    }
  };

  const showActiveSpacer =
    isContextual &&
    ((searchBarFocus && searchText !== "") || (isDropdownOpen && searchText !== ""));

  const activeSearchStyle = () => {
    if (loading) {
      return styles.activeSearchLoading;
    }

    const lengthToStylesMap = {
      0: styles.activeSearchNoResults,
      1: styles.activeSearchOneResult,
      2: styles.activeSearchTwoResults,
      3: styles.activeSearchThreeResults,
      4: styles.activeSearchFourResults,
      5: styles.activeSearchFiveResults,
      6: styles.activeSearchSixResults,
    };

    return lengthToStylesMap[resultsData.length] || styles.activeSearchNoResults;
  };

  return (
    <Dropdown
      isFullWidth
      closeOnItemSelect={false}
      isOpen={isDropdownOpen}
      triggerCallback={setIsDropdownOpen}
    >
      <DropdownTrigger>
        <div css={[styles.searchBar, showActiveSpacer && activeSearchStyle()]}>
          <TextField
            isFocused={searchBarFocus}
            onChange={e => onSearch(e.target.value)}
            onFocus={handleFocus}
            onBlur={() => setSearchBarFocus(false)}
            size="small"
            placeholder={t(l.sharedCatalog.filter.SearchIngredients)}
            value={searchText}
            onKeyDown={onKeyDown}
            inputIcon={faSearch}
            iconPosition="end"
          />
        </div>
      </DropdownTrigger>
      {searchResults()}
    </Dropdown>
  );
};

export { IngredientSearch };
